import { Fragment, type ReactNode } from 'react';
import { ENTITY_TOKEN, type MentionKind } from '@tradeoutlet/shared';
import { EntityChip } from './EntityChip';
import { cachedMention } from '@/lib/entityMentions';
import { PersonButton } from '@/features/people/PersonDrawer';
import { useMembers, useWorkspaceId } from '@/lib/hooks';
import { api } from '@/lib/api';
import { useUi } from '@/lib/store';
import { useAuth } from '@/lib/auth';

const TOKEN = /(<@[0-9a-f-]{36}>|<#(?:building|task|project|journey|issue|channel):[0-9a-f-]{36}>|\bhttps?:\/\/[^\s<]+|\b[A-Z][A-Z0-9]{1,9}-\d{1,7}\b|`[^`\n]+`|\*\*[^*\n]+\*\*|_[^_\n]+_)/g;

function TaskRef({ refKey }: { refKey: string }) {
  const ws = useWorkspaceId();
  const openTask = useUi(s => s.openTask);
  const toast = useUi(s => s.toast);
  const open = async () => {
    if (!ws) return;
    const r = await api.tasksByKeys(ws, [refKey]).catch(() => null);
    const t = r?.tasks[0];
    if (t) openTask(t.id); else toast(`No task ${refKey} found (or you can't see it)`, 'error');
  };
  return <button onClick={() => void open()} className="rounded bg-emerald-50 px-1 font-mono text-[0.9em] text-emerald-700 hover:underline dark:bg-emerald-900/40 dark:text-emerald-300">{refKey}</button>;
}

function Inline({ text }: { text: string }) {
  const { byId } = useMembers();
  const { userId } = useAuth();
  const parts = text.split(TOKEN);
  return (
    <>
      {parts.map((p, i) => {
        if (i % 2 === 0) return <Fragment key={i}>{p}</Fragment>;
        if (p.startsWith('<@')) {
          const id = p.slice(2, -1);
          return <PersonButton key={i} userId={byId.has(id) ? id : null} name={byId.get(id)?.display_name} className={id === userId ? 'mention mention-me hover:underline' : 'mention hover:underline'}><span className={byId.has(id) ? undefined : 'mention'}>@{byId.get(id)?.display_name ?? 'unknown'}</span></PersonButton>;
        }
        if (p.startsWith('<#')) { const [kind, id] = p.slice(2, -1).split(':'); return <EntityChip key={i} kind={kind as MentionKind} id={id.toLowerCase()} />; }
        if (p.startsWith('http')) return <a key={i} href={p} target="_blank" rel="noreferrer noopener" className="link">{p}</a>;
        if (p.startsWith('`')) return <code key={i} className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[0.85em] text-rose-600 dark:bg-slate-800 dark:text-rose-300">{p.slice(1, -1)}</code>;
        if (p.startsWith('**')) return <strong key={i}>{p.slice(2, -2)}</strong>;
        if (p.startsWith('_')) return <em key={i}>{p.slice(1, -1)}</em>;
        return <TaskRef key={i} refKey={p} />;
      })}
    </>
  );
}

/** Lightweight, XSS-safe message rendering (no HTML is ever injected). */
export function MessageBody({ body }: { body: string }) {
  const blocks: ReactNode[] = [];
  const segments = body.split(/```/);
  segments.forEach((seg, i) => {
    if (i % 2 === 1) {
      blocks.push(<pre key={i} className="my-1 overflow-x-auto rounded-md bg-slate-900 p-3 font-mono text-xs text-slate-100">{seg.replace(/^\n/, '')}</pre>);
    } else if (seg) {
      // "> " lines are quotes (quote-replies in threads); everything else is inline text.
      const parts = seg.split(/((?:^|\n)(?:> [^\n]*(?:\n|$))+)/);
      parts.forEach((part, j) => {
        if (!part) return;
        if (/^\n?> /.test(part)) {
          const lines = part.replace(/^\n/, '').replace(/\n$/, '').split('\n').map(l => l.slice(2));
          blocks.push(<blockquote key={`${i}-${j}`} className="my-0.5 border-l-[3px] border-slate-300 pl-2 text-slate-600 dark:border-slate-600 dark:text-slate-300" data-testid="message-quote">
            {lines.map((l, k) => <span key={k} className="block whitespace-pre-wrap break-words"><Inline text={l} /></span>)}</blockquote>);
        } else blocks.push(<span key={`${i}-${j}`} className="whitespace-pre-wrap break-words"><Inline text={part} /></span>);
      });
    }
  });
  return <div className="text-[14.5px] leading-relaxed" data-message-body>{blocks}</div>;
}

/** Plain-text preview with mentions resolved (search results, notifications). */
export function usePlainText() {
  const { byId } = useMembers();
  const ws = useWorkspaceId();
  return (body: string) => body.replace(ENTITY_TOKEN, (_t, k: string, id: string) => `#${cachedMention(ws, k.toLowerCase() as MentionKind, id.toLowerCase())?.label.replace(/^#/, '') ?? k.toLowerCase()}`).replace(/<@([0-9a-f-]{36})>/g, (_, id: string) => `@${byId.get(id)?.display_name ?? 'unknown'}`);
}
